import React, { useState, useEffect } from "react";
import { WeeklyTimelineDay, ItemType } from "../../models/weeklyTimeline";
import WeeklyTimelineItem from "./WeeklyTimelineItem";
import { Location as TQSLocation } from "../../models/location";
import { RecentActivityFilter } from "@/models/recentActivityFilter";

type ITimeLinesLocation = {
  location: TQSLocation;
  timeline: WeeklyTimelineDay[];
};

// Weekly Timeline Chart, loops over each timeline and creates a display for each day
interface WeeklyTimelineProps {
  timelinesWithLocations: ITimeLinesLocation[];
  allLocations: TQSLocation[];
  selectedLocation: TQSLocation;
  timezone: string;
  filters: RecentActivityFilter;
}

const filterItem = (item: ItemType, filters: RecentActivityFilter) => {
  const result = (() => {
    switch (item.type) {
      case "Quality Alert":
        return filters.qualityAlert;
      case "Supplier Note":
        return filters.supplierNote;
      case "Daily Note":
        return filters.dailyNote;
      case "Proactive Audit":
        return filters.proactiveAudit;
      case "Additional Hours":
        return filters.additionalHours;
      case "Check In":
        return filters.checkedIn;
      case "Production Volume":
        return filters.dailyProductionVolume;
      default:
        return true;
    }
  })();

  return result;
};

const filterTimeline = (
  timeline: WeeklyTimelineDay[],
  filters: RecentActivityFilter,
) => {
  return timeline.map((day) => ({
    ...day,
    items: day.items.filter((item) => filterItem(item, filters)),
  }));
};

const WeeklyTimelineChart: React.FC<WeeklyTimelineProps> = ({
  timelinesWithLocations,
  timezone,
  filters,
}) => {
  const [filteredTimelinesWithLocations, setFilteredTimelinesWithLocations] =
    useState(timelinesWithLocations);

  useEffect(() => {
    const newFilteredTimelinesWithLocations = timelinesWithLocations.map(
      ({ location, timeline }) => {
        const filteredTimeline = filterTimeline(timeline, filters);
        return {
          location,
          timeline: filteredTimeline,
        };
      },
    );
    setFilteredTimelinesWithLocations(newFilteredTimelinesWithLocations);
  }, [timelinesWithLocations, filters]);

  return (
    <div className="h-full w-full max-w-full overflow-y-auto overflow-x-hidden">
      {filteredTimelinesWithLocations.map(({ location, timeline }, index) => {
        const locationName = location ? location.name : "Unknown Location";

        return (
          <SingleTimelineChart
            key={index}
            locationName={locationName}
            timeline={timeline}
            timezone={timezone}
          />
        );
      })}
    </div>
  );
};
interface SingleTimelineProps {
  locationName: string;
  timeline: WeeklyTimelineDay[];
  timezone: string;
}

const SingleTimelineChart: React.FC<SingleTimelineProps> = ({
  locationName,
  timeline,
  timezone,
}) => {
  const [openDays, setOpenDays] = useState(Array(timeline.length).fill(true));

  const toggleDayOpen = (index: number) => {
    const newOpenDays = [...openDays];
    newOpenDays[index] = !newOpenDays[index];
    setOpenDays(newOpenDays);
  };

  return (
    <div className="mt-[6px] h-full w-full max-w-full overflow-x-hidden">
      <h2 className="mb-2 text-[14px] font-semibold">{locationName}</h2>
      <div className="mb-2 border border-gray-400"></div>
      {timeline.map((day, index) => {
        const dateObject = new Date(day.date);
        const isOpen = openDays[index];

        const monthDay = dateObject.toLocaleDateString("en-US", {
          month: "numeric",
          day: "2-digit",
          timeZone: "UTC",
        });

        return (
          <div key={index} className="flex-shrink-0">
            <div
              className="flex cursor-pointer items-center justify-between text-lg font-semibold"
              onClick={() => toggleDayOpen(index)}
            >
              <h1 className="text-[14px] text-[#212322]">
                {dateObject.toLocaleDateString("en-US", {
                  weekday: "long",
                  timeZone: "UTC",
                })}{" "}
                ({monthDay})
              </h1>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                className={`h-6 w-6 text-[#212322] transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
              >
                <path
                  fillRule="evenodd"
                  d="M12.53 16.28a.75.75 0 0 1-1.06 0l-7.5-7.5a.75.75 0 0 1 1.06-1.06L12 14.69l6.97-6.97a.75.75 0 1 1 1.06 1.06l-7.5 7.5Z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            {isOpen && (
              <div>
                {day.items.map((item, itemIndex) => (
                  <WeeklyTimelineItem
                    key={itemIndex}
                    item={item}
                    isLast={itemIndex === day.items.length - 1}
                    timezone={timezone}
                  />
                ))}
                {day.items.filter((i) => i.type !== "Check In").length ===
                  0 && (
                  <div className="flex h-[30px] flex-col items-start justify-start text-[12px] text-[#212322]">
                    <div className="font-semibold">✅ Coverage Confirmed</div>
                    <div className="font-normal italic text-[10px] text-[#212322] pl-5">
                      No additional updates logged today.
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default WeeklyTimelineChart;
